import { LitElement, TemplateResult, css, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { Ref, createRef, ref } from 'lit/directives/ref.js';

import { ANIMATION_SPEED, BREAKPOINT_MEDIUM_PX } from '@/settings';
import { baseStyles } from '@/styles';
import { MessageTypes } from '@/types';
import { animationsComplete } from '@/utils';
import {
  iconCheckmarkCircle,
  iconExclamationCircle,
  iconExclamationTriangle,
  iconX,
} from '../icons';

@customElement('fg-message-snack-bar')
export class FGMessageSnackBar extends LitElement {
  private readonly MESSAGE_ICONS: {
    [key in MessageTypes]: TemplateResult;
  } = {
    error: iconExclamationCircle,
    success: iconCheckmarkCircle,
    warning: iconExclamationTriangle,
  };

  fgMessagePopover: Ref<HTMLElement> = createRef();

  /**
   * Message to show in the message bar
   *
   * @property message
   */
  @property({ type: String })
  message? = '';

  /**
   * Type of message to show this will handle the styling to be error or success
   *
   * @property type
   * @type {MessageTypes}
   */
  @property({ type: String })
  type: MessageTypes = MessageTypes.SUCCESS;

  @property({ type: Boolean })
  show = false;

  @property({ type: Boolean })
  autoDisappear = false;

  @state()
  icon?: TemplateResult;

  @state()
  invalidTimeout?: ReturnType<typeof setTimeout>;

  willUpdate = () => {
    if (this.show) {
      this.fgMessagePopover.value?.showPopover();
      this.icon = this.MESSAGE_ICONS[this.type || 'success'];

      if (this.autoDisappear) {
        clearTimeout(this.invalidTimeout);
        this.invalidTimeout = setTimeout(() => this.closeMessageBar(), 2000);
      }

      return;
    }

    if (!this.show) {
      this.closeMessageBar();
    }
  };

  closeMessageBar = async () => {
    if (this.fgMessagePopover.value) {
      this.fgMessagePopover.value?.setAttribute('loading', '');

      await animationsComplete(this.fgMessagePopover.value);
      this.fgMessagePopover.value?.hidePopover();
      this.fgMessagePopover.value?.removeAttribute('loading');
      this.onCloseMessageBar();
    }
  };

  onCloseMessageBar = () => {
    this.dispatchEvent(new CustomEvent('on-close', { composed: true }));
  };

  render() {
    return html`
      <div
        popover="manual"
        ${ref(this.fgMessagePopover)}
        class="${classMap({
          [this.type]: this.type,
        })}"
      >
        <p class="message">
          <span class="icon">${this.icon}</span>
          <span>${this.message}</span>
        </p>
        <button @click=${this.closeMessageBar}>${iconX}</button>
      </div>
    `;
  }

  static styles = [
    baseStyles,
    css`
      div[popover] {
        position: fixed;
        top: 20px;
        left: 50%;
        width: 90vw;
        background-color: #fff;
        padding: 8px;
        border-radius: var(
          --fg-borderradius--xs,
          var(--fg-fallback-borderradius--xs)
        );
        margin: 0;
        max-width: 1248px;
      }

      div[popover]:popover-open {
        display: flex;
        transform: translateX(-50%);
        animation: fade-in ${ANIMATION_SPEED}s ease-in;
      }

      div[loading]:popover-open {
        animation: fade-out ${ANIMATION_SPEED}s ease-in;
      }

      .popover::backdrop {
        display: none;
      }

      div[popover].error {
        background-color: var(--fg-error--light);
        border: 1px solid var(--fg-error);
        color: var(--fg-error);
      }

      div[popover].success {
        background-color: var(--fg-success--light);
        border: 1px solid var(--fg-success);
        color: var(--fg-success);
      }

      @supports not selector(:popover-open) {
        div[popover]:not(.\\:popover-open) {
          display: none;
        }

        div[popover].\\:popover-open {
          display: flex;
          display: flex;
          top: 20px;
          transform: translateX(-50%);
          animation: fade-in ${ANIMATION_SPEED}s ease-in;
        }

        div[loading].\\:popover-open {
          animation: fade-out ${ANIMATION_SPEED}s ease-in;
        }
      }

      .message {
        display: flex;
        gap: 8px;
        overflow: hidden;
      }

      .message span {
        display: flex;
        align-items: center;
      }

      .icon {
        margin-left: 8px;
      }

      button {
        display: grid;
        place-items: center;
        margin-left: auto;
      }

      .popover.error button {
        color: var(--fg-error);
      }

      .popover.success button {
        color: var(--fg-success);
      }

      @media screen and (min-width: ${BREAKPOINT_MEDIUM_PX}px) {
        .popover {
          width: 70vw;
          padding: 20px 16px;
        }

        .message {
          overflow: unset;
        }
      }

      @keyframes fade-in {
        from {
          opacity: 0;
        }
        to {
          opacity: 1;
        }
      }

      @keyframes fade-out {
        from {
          opacity: 1;
        }
        to {
          opacity: 0;
        }
      }
    `,
  ];
}
